import {
  Box,
  Icon,
  Menu,
  PopoverProps,
  Tooltip,
  Typography,
  useTheme
} from "@outschool/backpack";
import {
  UpcomingChargesQueryQuery,
  UpcomingChargesQueryQueryVariables
} from "@outschool/gql-frontend-generated";
import {
  faArrowRight,
  faCaretDown,
  faCaretRight,
  faInfoCircle
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { transactionsPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { CreditIcon, TrackedButton } from "@outschool/ui-components-shared";
import { CoinIcon } from "@outschool/ui-components-website";
import { Screen, useNavigation } from "@outschool/ui-utils";
import { groupBy, partition, sumBy } from "lodash";
import React from "react";

import {
  EsaMembershipCreditBalance,
  useEsaMembershipCreditBalances
} from "../../hooks/useEsaMembershipCreditBalance";
import {
  SubscriptionCreditBalance,
  useSubscriptionCreditBalance
} from "../../hooks/useSubscriptionCreditBalance";
import { UPCOMING_CHARGES_QUERY } from "./SubscriptionCreditNavItem";

const ESA_MEMBERSHIP_UPCOMING_CHARGE_WEEK = 4;

type ModalPosition = {
  anchorOrigin: PopoverProps["anchorOrigin"];
  transformOrigin: PopoverProps["transformOrigin"];
};

type UpcomingChargesAgg = {
  priceCents: number;
  priceCredits: number;
  numEnrollments: number;
};

export function EsaMembershipNavItem() {
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\components\\nav\\EsaMembershipNavItem");
  const theme = useTheme();
  const [showModal, setShowModal] = React.useState<null | HTMLElement>(null);
  const [start, end] = React.useMemo(() => {
    return [
      dayjs().toDate(),
      dayjs().add(ESA_MEMBERSHIP_UPCOMING_CHARGE_WEEK, "weeks").toDate()
    ];
  }, []);

  const {
    esaUpcomingChargesPerLearner,
    nonEsaUpcomingCharges,
    loading: loadingUpcomingCharges
  } = useUpcomingChargesPerLearner(start, end);

  const subscriptionCreditBalance = useSubscriptionCreditBalance();

  const { creditBalances, loading: loadingEsaMembershipCredits } =
    useEsaMembershipCreditBalances();

  const isSmallScreen = Screen.useIsSmall();

  if (
    loadingEsaMembershipCredits ||
    loadingUpcomingCharges ||
    subscriptionCreditBalance.loading
  ) {
    return null;
  }

  const allAvailableCredits =
    sumBy(creditBalances, "availableCredits") +
    (subscriptionCreditBalance
      ? subscriptionCreditBalance.availableCredits
      : 0);
  const navItemTextColor = theme.palette.common.black;

  const modalPosition: ModalPosition = isSmallScreen
    ? {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" }
      }
    : {
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        transformOrigin: { vertical: "top", horizontal: 250 }
      };

  return (
    <Box>
      <TrackedButton
        variant="link"
        trackingName="esa_membership_nav_item"
        sx={{
          border: "none",
          paddingX: 8,
          paddingTop: 6,
          display: "block",
          color: theme.palette.common.black,
          [theme.breakpoints.down("sm")]: {
            display: "flex",
            margin: "auto",
            paddingTop: "18px"
          }
        }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          setShowModal(e.currentTarget);
        }}
      >
        <Box flex={true}>
          <Box sx={{ paddingTop: "0.2rem", paddingRight: "0.5rem" }}>
            <CreditIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
              sx={{ width: "2.4rem", height: "2.4rem" }}
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: navItemTextColor,
              fontWeight: "600"
            }}
          >
            {`${allAvailableCredits}`}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            [theme.breakpoints.down("sm")]: {
              paddingLeft: "0.5rem"
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: navItemTextColor
            }}
          >
            {t("Credits")}
          </Typography>
        </Box>
        <EsaMembershipModal
          showModal={showModal}
          setShowModal={setShowModal}
          modalPosition={modalPosition}
          navItemTextColor={navItemTextColor}
          creditBalances={creditBalances}
          availableCredits={allAvailableCredits}
          subscriptionCreditBalance={
            currentUser?.hasSubscription ? subscriptionCreditBalance : null
          }
          nonEsaUpcomingCharges={nonEsaUpcomingCharges}
          upcomingChargesPerLearner={esaUpcomingChargesPerLearner}
        />
      </TrackedButton>
    </Box>
  );
}

type EsaMembershipModalArgs = {
  showModal: HTMLElement | null;
  setShowModal: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  modalPosition: ModalPosition;
  navItemTextColor: string;
  creditBalances: EsaMembershipCreditBalance[];
  subscriptionCreditBalance: SubscriptionCreditBalance | null;
  availableCredits: number;
  nonEsaUpcomingCharges?: UpcomingChargesAgg;
  upcomingChargesPerLearner?: Record<string, UpcomingChargesAgg>;
};

function EsaMembershipModal({
  showModal,
  setShowModal,
  modalPosition,
  navItemTextColor,
  creditBalances,
  subscriptionCreditBalance,
  availableCredits,
  nonEsaUpcomingCharges,
  upcomingChargesPerLearner
}: EsaMembershipModalArgs) {
  const theme = useTheme();
  const navigate = useNavigation();
  const { t } = useTranslation("client\\components\\nav\\EsaMembershipNavItem");
  const totalCreditsRequired =
    (nonEsaUpcomingCharges?.priceCredits ?? 0) +
    (upcomingChargesPerLearner
      ? sumBy(Object.values(upcomingChargesPerLearner).flat(), "priceCredits")
      : 0);

  return (
    <Menu
      anchorEl={showModal}
      open={Boolean(showModal)}
      onClose={(e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setShowModal(null);
      }}
      anchorOrigin={modalPosition.anchorOrigin}
      transformOrigin={modalPosition.transformOrigin}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 16,
            paddingX: 8,
            maxWidth: "420px",
            width: "420px",
            boxShadow: "0px 0px 4em rgba(0, 0, 0, 0.2)",
            [theme.breakpoints.down("sm")]: {
              maxWidth: "calc(100% - 32px);"
            }
          }
        }
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(101deg, #4B01D4 -29.61%, #380596 58.3%)",
          boxShadow:
            "0px 0px 4px 0px rgba(92, 76, 236, 0.66), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)",
          borderRadius: "16px",
          padding: 16,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          color: theme.palette.common.white,
          "&:hover": {
            cursor: "pointer"
          },
          marginBottom: "1rem",
          alignItems: "flex-start"
        }}
        onClick={() => {
          navigate(transactionsPath());
          setShowModal(null);
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", display: "block" }}
          >
            {t("Available Credits")}
          </Typography>
          <Box
            sx={{
              marginTop: "1.5rem"
            }}
          >
            <Typography>{t("Manage Credits")}</Typography>&nbsp;
            <Icon
              icon={faArrowRight}
              sx={{ color: theme.palette.common.white }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end"
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 8,
              padding: "0.8rem 1.6rem",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: 20
            }}
          >
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />{" "}
            <Typography
              variant="h1"
              sx={{
                marginLeft: 4,
                color: navItemTextColor
              }}
            >
              {availableCredits}
            </Typography>
          </Box>
        </Box>
      </Box>
      {subscriptionCreditBalance && (
        <MembershipCreditOutlook
          creditBalance={subscriptionCreditBalance}
          totalCreditsRequired={totalCreditsRequired}
        />
      )}
      <EsaCreditOutlook creditBalances={creditBalances} />
    </Menu>
  );
}

function MembershipCreditOutlook({
  creditBalance,
  totalCreditsRequired
}: {
  creditBalance: SubscriptionCreditBalance;
  totalCreditsRequired: number;
}) {
  const { t } = useTranslation("client\\components\\nav\\EsaMembershipNavItem");
  const theme = useTheme();
  const [expand, setExpand] = React.useState<boolean>(false);
  const tooltipText = t(
    "This total includes classes eligible for program-funded credits. If a class qualifies for program-funded credits, those will be used first."
  );

  return (
    <Box
      sx={{
        borderBottom: "solid 1px",
        borderColor: theme.palette.neutral[300],
        marginX: "0.7rem",
        "&:last-child": {
          borderBottom: "none"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexdirection: "row",
          justifyContent: "flex-start",
          paddingY: "0.7rem",
          cursor: "pointer",
          alignItems: "baseline"
        }}
        onClick={() => {
          setExpand(prev => !prev);
        }}
      >
        <Box>
          <Icon
            icon={expand ? faCaretDown : faCaretRight}
            sx={{
              marginRight: "0.3rem"
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between"
            }}
          >
            <Typography variant="subtitle1">{t("Membership")}</Typography>
            <Box>
              <Typography>
                {t("{{availableCredits}} credits", {
                  availableCredits: creditBalance.availableCredits
                })}
              </Typography>
            </Box>
          </Box>
          {expand && (
            <Box
              sx={{
                color: theme.palette.neutral[600]
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  paddingY: "0.8rem"
                }}
              >
                <Typography variant="body1">
                  {t("Credit needed for this period:")}{" "}
                  <b>
                    {totalCreditsRequired} {t("credits")}
                  </b>
                  <Tooltip placement="top" title={tooltipText}>
                    <Icon
                      icon={faInfoCircle}
                      sx={{
                        marginLeft: "0.5rem",
                        color: theme.palette.primary[800]
                      }}
                    />
                  </Tooltip>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingY: "0.8rem"
                }}
              >
                <>
                  <Typography variant="body1">
                    {t("{{creditLimit}}-credits plan for the whole family.", {
                      creditLimit: creditBalance.subscriptionCreditLimit
                    })}
                    <br />
                    {t("Renew on")}{" "}
                    <b>
                      {t("{{renewDate}}", {
                        renewDate: dayjs(creditBalance.billingCycleEnd).format(
                          "MMM DD, YYYY"
                        )
                      })}
                    </b>
                  </Typography>
                </>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function EsaCreditOutlook({
  creditBalances
}: {
  creditBalances: EsaMembershipCreditBalance[];
}) {
  const { t } = useTranslation("client\\components\\nav\\EsaMembershipNavItem");
  const theme = useTheme();
  const [showLearnerCredit, setShowLearnerCredit] =
    React.useState<Boolean>(false);

  const allAvailableCredits = sumBy(creditBalances, "availableCredits");

  return (
    <Box
      sx={{
        borderBottom: "solid 1px",
        borderColor: theme.palette.neutral[300],
        marginX: "0.7rem",
        "&:last-child": {
          borderBottom: "none"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexdirection: "row",
          justifyContent: "flex-start",
          paddingY: "0.7rem",
          cursor: "pointer",
          alignItems: "baseline"
        }}
        onClick={() => {
          setShowLearnerCredit(prev => !prev);
        }}
      >
        <Box>
          <Icon
            icon={showLearnerCredit ? faCaretDown : faCaretRight}
            sx={{
              marginRight: "0.3rem"
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between"
            }}
          >
            <Typography variant="subtitle1">
              {t("Program funded credits")}
            </Typography>
            <Box>
              <Typography>
                {t("{{availableCredits}} credits", {
                  availableCredits: allAvailableCredits
                })}
              </Typography>
            </Box>
          </Box>
          {showLearnerCredit && (
            <Box
              sx={{
                color: theme.palette.neutral[600]
              }}
            >
              {creditBalances.map((creditBalance, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingY: "0.8rem",
                    borderBottom: "1px solid",
                    borderColor: theme.palette.neutral.light,
                    "&:first-child": {
                      marginTop: "0.5rem",
                      borderTop: "1px solid",
                      borderColor: theme.palette.neutral.light
                    }
                  }}
                >
                  <Typography variant="body1">{creditBalance.name}</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      textOverflow: "ellipsis ellipsis"
                    }}
                  >
                    {creditBalance.programName}
                  </Typography>
                  <Typography variant="body1">
                    {creditBalance.availableCredits} {t("credits")}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export const useUpcomingChargesPerLearner = (
  start: Date | null,
  end: Date | null
) => {
  const { data, loading, error } = useQueryWithPreviousData<
    UpcomingChargesQueryQuery,
    UpcomingChargesQueryQueryVariables
  >(UPCOMING_CHARGES_QUERY, {
    variables: {
      start,
      end,
      includeLearner: true
    },
    skip: !start || !end
  });

  const [nonEsaUpcomingCharges, esaUpcomingChargesPerLearner] =
    React.useMemo(() => {
      if (!data) {
        return [];
      }

      const esaAggregatedData: {
        [learnerUid: string]: {
          priceCents: number;
          priceCredits: number;
          numEnrollments: number;
        };
      } = {};

      const [esaCharges, nonEsaCharges] = partition(
        data.currentUser?.activeEnrollments.filter(Boolean),
        enrollment => enrollment!.canUseEsaMembership
      );

      const nonEsaAggregatedData = nonEsaCharges
        .flatMap(enrollment => enrollment?.upcomingCharges)
        .reduce(
          (acc, charge) => {
            return {
              priceCents: acc.priceCents + (charge?.priceCents ?? 0),
              priceCredits: acc.priceCredits + (charge?.priceCredits ?? 0),
              numEnrollments: acc.numEnrollments + 1
            };
          },
          {
            priceCents: 0,
            priceCredits: 0,
            numEnrollments: 0
          }
        );

      const byLearner = groupBy(
        esaCharges,
        enrollment => enrollment!.learner?.uid
      );

      for (const [learnerUid, enrollments] of Object.entries(byLearner)) {
        const upcomingCharges = enrollments.flatMap(
          enrollment => enrollment?.upcomingCharges
        );

        esaAggregatedData[learnerUid] = upcomingCharges.reduce(
          (acc, charge) => {
            return {
              priceCents: acc.priceCents + (charge?.priceCents ?? 0),
              priceCredits: acc.priceCredits + (charge?.priceCredits ?? 0),
              numEnrollments: acc.numEnrollments + 1
            };
          },
          {
            priceCents: 0,
            priceCredits: 0,
            numEnrollments: 0
          }
        );
      }

      return [nonEsaAggregatedData, esaAggregatedData];
    }, [data]);

  return {
    esaUpcomingChargesPerLearner,
    nonEsaUpcomingCharges,
    loading,
    error
  };
};
